import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { SceneGroup } from '@lumen5/luminary';
import type { LexicalEditor } from 'lexical';
import { $createRangeSelection, $getRoot, $setSelection, SELECTION_CHANGE_COMMAND } from 'lexical';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { parseReplacementWithPauses, sameWords } from './pause-tags';
import {
    computeSceneGroupShortIds,
    resolveSceneGroupById,
    resolveSceneGroupHandle,
} from './scene-group-addressing';
import {
    buildPatternTokens,
    buildTokenStreamFromEditor,
    findTokenSequenceMatches,
    type MatchRange,
    matchRangeForTokens,
    setPausesInRange,
    SPAN_NOT_FOUND_ERROR,
} from './transcript-pause-staging';
import { findAllWordSequenceMatches, type MatchableWord } from './transcript-word-match';
import { VideoStateTool } from './VideoStateTool';
import { AI_VOICEOVER_DISABLED_MESSAGE } from './constants';

import type { AudioAssetSourceData } from 'js/src/client';
import { getCanUseAIVoiceover } from 'js/src/data/account/account-selectors';
import { AI_VOICE_SPEED_OPTIONS, type AIVoiceSpeedName } from 'js/src/data/ai-voices-and-avatars';
import { isSceneGroupLoading } from 'js/src/data/app';
import {
    editAIVoiceoverOperation,
    editFullAIVoiceoverOperation,
    getEditorFromSceneGroupId,
    getFullTranscriptFromEditor,
    getOriginalTranscriptLanguage,
} from 'js/src/data/transcript';
import { $isTranscriptWordNodeAndNotSilence } from 'js/src/data/transcript/nodes/SilenceNode';
import { $isTextCorrectElementNode } from 'js/src/data/transcript/nodes/TextCorrectElementNode';
import type { TranscriptWordNode } from 'js/src/data/transcript/nodes/TranscriptWordNode';
import { $isTranscriptWordNode } from 'js/src/data/transcript/nodes/TranscriptWordNode';
import {
    CANCEL_CORRECTION_COMMAND,
    SAVE_ACTIVE_CORRECTION_COMMAND,
    START_CORRECT_TEXT_COMMAND,
    TextCorrectionTrigger,
} from 'js/src/data/transcript/transcript-types';
import { $dfsWithinNode, $isSpace, updateEditor } from 'js/src/data/transcript/transcript-utils';
import { refreshFirstScenePreviewOperation } from 'js/src/data/video-planner';
import type { RootState } from 'js/src/libs/RootState';
import uuid from 'js/src/libs/uuid';
import { $deleteAllScenesWithoutWordsOperation } from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import {
    canSceneGroupUseEditAIVO,
    getVoiceDrivenAssetSourceData,
    getVoiceDrivenBaseAssetCutRegions,
    getVoiceDrivenBaseAssetSourceDuration,
    isAIVoiceoverSceneGroup,
} from 'js/src/luminary/luminary-selectors';

type VoiceSpeed = AIVoiceSpeedName;

// Named speed step → the multiplier the backend stores, sourced from the same canonical
// list the speed selector UI uses.
const VOICE_SPEED_TO_MULTIPLIER = Object.fromEntries(
    AI_VOICE_SPEED_OPTIONS.map((option) => [option.name, option.value]),
) as Record<VoiceSpeed, number>;
const VOICE_SPEED_ENUM = AI_VOICE_SPEED_OPTIONS.map((option) => option.name) as [
    VoiceSpeed,
    ...VoiceSpeed[],
];
const VOICE_SPEED_DESCRIPTION = `A new voice speed. One of: ${AI_VOICE_SPEED_OPTIONS.map(
    (option) => `"${option.name}" (${option.displayRate})`,
).join(', ')}.`;

export const editAIVoiceoverSchema = z.object({
    scene_group_id: z
        .string()
        .describe(
            'The stable scene_group_id of the AI voiceover scene group to edit (from the video state).',
        ),
    find_phrase: z
        .string()
        .optional()
        .describe(
            'A run of consecutive tokens from the current transcript to replace, written exactly as they appear. Must be provided together with replacement.',
        ),
    replacement: z
        .string()
        .optional()
        .describe(
            'The new token string (words and `<pause seconds="N"/>` tags) to put in place of find_phrase. Must be provided together with find_phrase.',
        ),
    voice_slug: z
        .string()
        .optional()
        .describe(
            `A new AI voice slug (from the ${TOOL_NAMES.searchVoices} tool) to re-synthesize the voiceover with.`,
        ),
    voice_speed: z.enum(VOICE_SPEED_ENUM).optional().describe(VOICE_SPEED_DESCRIPTION),
});
export type EditAIVoiceoverArgs = z.infer<typeof editAIVoiceoverSchema>;

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export class EditAIVoiceoverTool extends VideoStateTool<typeof editAIVoiceoverSchema> {
    protected override readonly schema = editAIVoiceoverSchema;
    protected override readonly toolName = TOOL_NAMES.editAiVoiceover;
    protected override readonly toolDescription = `Edit an existing AI voiceover on a voice-driven scene group. Load the \`${SKILL_NAMES.aiVoiceoverVideoBasics}\` skill for how to use.`;

    public override getToolDisplayName(): string {
        return 'Editing voiceover';
    }

    protected override onEditApplied(args: EditAIVoiceoverArgs): void {
        // Re-synthesizing the first scene group's voiceover can realign its scenes and change the
        // video thumbnail. Resolve defensively — this runs after a successful edit, so it must never
        // throw on an id that no longer resolves.
        const resolved = resolveSceneGroupById(this.luminary, args.scene_group_id);
        if (!('error' in resolved) && resolved.index === 0) {
            this.dispatch(refreshFirstScenePreviewOperation());
        }
    }

    public get statusIcon(): string {
        return 'record_voice_over';
    }

    public async execute(args: EditAIVoiceoverArgs, _context: ToolExecutionContext) {
        if (!getCanUseAIVoiceover(this.getState())) {
            return `Error: ${AI_VOICEOVER_DISABLED_MESSAGE}`;
        }

        const { find_phrase, replacement, voice_slug, voice_speed } = args;

        const { sceneGroup } = resolveSceneGroupHandle(this.luminary, args);
        const shortId =
            computeSceneGroupShortIds(this.luminary.sceneGroups.map((group) => group.id)).get(
                sceneGroup.id,
            ) ?? sceneGroup.id;

        if (!isAIVoiceoverSceneGroup(sceneGroup)) {
            return `Error: Scene group ${shortId} is not an AI voiceover scene group. ${TOOL_NAMES.editAiVoiceover} only works on AI voiceover scene groups, not talking-head or human/recorded voiceover groups.`;
        }

        const wantsTextEdit = find_phrase !== undefined || replacement !== undefined;
        const wantsVoiceChange = voice_slug !== undefined;
        const wantsSpeedChange = voice_speed !== undefined;

        if (!wantsTextEdit && !wantsVoiceChange && !wantsSpeedChange) {
            return 'Error: No edit requested. Provide at least one of: find_phrase + replacement (to reword), voice_slug (to change voice), or voice_speed (to change speed).';
        }
        if (wantsTextEdit && (find_phrase === undefined || replacement === undefined)) {
            return 'Error: find_phrase and replacement must be provided together.';
        }
        if (wantsTextEdit && replacement!.trim() === '') {
            return 'Error: replacement cannot be empty. Provide the new text to synthesize in place of find_phrase (removing words is not supported here).';
        }
        if (wantsSpeedChange && VOICE_SPEED_TO_MULTIPLIER[voice_speed] === undefined) {
            return `Error: voice_speed "${voice_speed}" is not allowed. Allowed values are: ${Object.keys(VOICE_SPEED_TO_MULTIPLIER).join(', ')}.`;
        }

        const originalTranscript = sceneGroup.originalTranscript;
        const transcriptWords: MatchableWord[] =
            originalTranscript?.transcriptData?.words || originalTranscript?.words || [];
        if (!originalTranscript || transcriptWords.length === 0) {
            return `Error: Scene group ${shortId} does not have a transcript to edit.`;
        }

        // Validate the phrase against the current transcript up front, before touching the
        // editor or UI, so bad phrases fail cleanly with no side effects. The transcript is one
        // token stream (words + pauses); find_phrase must match one run of it exactly and uniquely.
        let matchRange: MatchRange | null = null;
        if (wantsTextEdit) {
            if (!canSceneGroupUseEditAIVO(sceneGroup)) {
                return `Error: Scene group ${shortId} cannot be reworded because its voiceover predates AI voice metadata. This cannot be fixed by upgrading — do not retry or suggest an upgrade. Voice and speed changes may still work.`;
            }
            const editor = getEditorFromSceneGroupId(this.getState(), sceneGroup.id);
            if (!editor) {
                return `Error: The transcript editor for scene group ${shortId} is not available.`;
            }
            // Cut-aware (see $isInCutRegion) so find_phrase only matches words this group still speaks.
            const stream = buildTokenStreamFromEditor(
                editor,
                getVoiceDrivenBaseAssetCutRegions(sceneGroup),
            );
            const pattern = buildPatternTokens(parseReplacementWithPauses(find_phrase!));
            const matches = findTokenSequenceMatches(stream, pattern);
            if (matches.length === 0) {
                return `Error: The phrase "${find_phrase}" was not found in the transcript. Its words and pauses must appear consecutively exactly as shown — include every <pause/> that falls inside the run you are replacing.`;
            }
            if (matches.length > 1) {
                return `Error: The phrase "${find_phrase}" appears ${matches.length} times in the transcript, so it is ambiguous. Provide a longer, unique phrase.`;
            }
            matchRange = matchRangeForTokens(stream, matches[0]);
        }

        const changeSummary: string[] = [];

        try {
            if (wantsTextEdit) {
                const { words: findWords } = parseReplacementWithPauses(find_phrase!);
                const { words, pauses } = parseReplacementWithPauses(replacement!);
                if (words.length === 0) {
                    return 'Error: replacement must contain words (removing all words is not supported).';
                }
                const textChanged = !sameWords(words, findWords);

                // Stage 1 — words: reword with the pause tags stripped. Skipped when only the
                // pauses changed, so a pause-only edit doesn't needlessly re-synthesize speech.
                if (textChanged) {
                    await this.applyReword(sceneGroup.id, findWords.join(' '), words.join(' '));
                }

                // Stage 2 — pauses: set the silences in the matched (just-reworded) span to
                // match the replacement's tags (add/resize/remove). The span is re-located by its
                // replacement words + the word offset captured during the match, so it survives the
                // reword regenerating word-node ids.
                //
                // Stage 1 has already committed and re-synthesized the reword, so a failure to
                // re-locate the span must not be reported as a failed edit: the words did change.
                // Re-locating can legitimately miss when the segmentation backend re-splits the
                // corrected text, so the replacement's words no longer appear verbatim. A miss
                // mutates nothing, so when the replacement asked for no pauses there is nothing
                // lost and reporting failure would only send the agent into a retry loop over an
                // edit that already landed. A miss when pauses *were* requested still surfaces,
                // since silently dropping them would be wrong.
                const reconcile = await this.applyPauseReconcile(
                    sceneGroup,
                    words,
                    matchRange!,
                    pauses,
                );
                if (reconcile === 'span-not-found' && pauses.length > 0) {
                    return `Error: ${SPAN_NOT_FOUND_ERROR}`;
                }
                const pausesChanged = reconcile === true;

                if (textChanged && pausesChanged) {
                    changeSummary.push(`reworded "${find_phrase}" and adjusted pauses`);
                } else if (textChanged) {
                    changeSummary.push(`reworded "${find_phrase}" → "${words.join(' ')}"`);
                } else if (pausesChanged) {
                    changeSummary.push('adjusted pauses');
                }
            }

            if (wantsVoiceChange || wantsSpeedChange) {
                const newVoiceSpeed = wantsSpeedChange
                    ? VOICE_SPEED_TO_MULTIPLIER[voice_speed]
                    : null;
                const { editFullAIVoiceover } = makeOperationsCallable(
                    { editFullAIVoiceoverOperation },
                    this.luminary,
                    this.dispatch,
                    this.getState as () => RootState,
                );
                await editFullAIVoiceover(sceneGroup.id, newVoiceSpeed, voice_slug);
                if (wantsVoiceChange) {
                    changeSummary.push(`changed voice to "${voice_slug}"`);
                }
                if (wantsSpeedChange) {
                    changeSummary.push(`changed speed to ${voice_speed}`);
                }
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error editing AI voiceover: ${errorMessage}`;
        }

        const editedGroup = this.luminary.getSceneGroupById(sceneGroup.id) ?? sceneGroup;
        const durationMillis = getVoiceDrivenBaseAssetSourceDuration(editedGroup);
        const durationInfo =
            typeof durationMillis === 'number' && durationMillis > 0
                ? ` New voiceover duration: ${(durationMillis / 1000).toFixed(1)}s.`
                : '';

        return `Success: Edited AI voiceover on scene group ${shortId} (${changeSummary.join('; ')}).${durationInfo}`;
    }

    /**
     * Make the pauses inside the matched (just-reworded or unchanged) span match the tags in the
     * replacement, then re-synthesize just that span. Returns whether anything changed — a
     * no-op reconcile (pauses already matched) skips the re-synthesis entirely.
     *
     * Returns `'span-not-found'` rather than throwing when the span cannot be re-located, since
     * that leaves the editor untouched and the caller may be able to treat it as inconsequential.
     * Every other failure throws: those are only reachable once silence nodes have already been
     * added or removed, so the edit must not be reported as a success.
     */
    private async applyPauseReconcile(
        sceneGroup: SceneGroup,
        replacementWords: string[],
        matchRange: MatchRange,
        pauses: ReturnType<typeof parseReplacementWithPauses>['pauses'],
    ): Promise<boolean | 'span-not-found'> {
        const editor = getEditorFromSceneGroupId(this.getState(), sceneGroup.id);
        if (!editor) {
            throw new Error('the transcript editor for this scene group is not available.');
        }

        const parentActionGroup = uuid();
        const { $deleteAllScenesWithoutWords } = makeOperationsCallable(
            { $deleteAllScenesWithoutWordsOperation },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );

        const result = await setPausesInRange(editor, {
            spanWords: replacementWords,
            wordOffsetHint: matchRange.wordsBefore,
            ownsLeadingGap: matchRange.ownsLeadingGap,
            ownsTrailingGap: matchRange.ownsTrailingGap,
            desiredPauses: pauses,
            onSilenceRemoved: () =>
                $deleteAllScenesWithoutWords(sceneGroup.id, false, parentActionGroup),
        });
        if ('error' in result) {
            return 'span-not-found';
        }
        if (!result.changed) {
            return false;
        }

        const transcript = getFullTranscriptFromEditor(editor, result.userSelectedWordIds, {
            includeSilence: true,
        });
        const { editAIVoiceover } = makeOperationsCallable(
            { editAIVoiceoverOperation },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );
        await editAIVoiceover(
            {
                sceneGroupId: sceneGroup.id,
                transcript,
                langCode: getOriginalTranscriptLanguage(this.getState(), sceneGroup.id),
                originalVoiceoverAssetSourceData: getVoiceDrivenAssetSourceData(
                    sceneGroup,
                ) as AudioAssetSourceData,
            },
            false,
            parentActionGroup,
        );
        await this.waitForEditToFinish(sceneGroup.id);
        return true;
    }

    /**
     * Reword a phrase by driving the same text-correction flow a human uses in the
     * transcript UI: enter correct mode on the scene group's (already-mounted) editor,
     * replace the matched words, then save — which segments the new text on the backend,
     * splices the new word nodes into the editor, and re-synthesizes the changed span via
     * editAIVoiceoverOperation. Going through the real flow (rather than assembling a
     * request) keeps word ids consistent so overlay/scene reconciliation resolves correctly.
     */
    private async applyReword(
        sceneGroupId: string,
        findPhrase: string,
        replacement: string,
    ): Promise<void> {
        // The Creator keeps the transcript (and its correction plugins) mounted, so we use
        // the live editor directly — no need to open a tab or move the user's focus.
        const editor = getEditorFromSceneGroupId(this.getState(), sceneGroupId);
        if (!editor) {
            throw new Error(
                'the transcript editor for this scene group is not available, so it cannot be reworded.',
            );
        }

        await this.selectWordRun(editor, findPhrase);
        editor.dispatchCommand(START_CORRECT_TEXT_COMMAND, TextCorrectionTrigger.BUTTON);
        try {
            await this.replaceWordsInCorrectionNode(editor, findPhrase, replacement);
        } catch (error) {
            // We entered correction mode but failed before saving; dismiss it so the user
            // isn't left staring at a stuck, half-applied correction in the mounted editor.
            editor.dispatchCommand(CANCEL_CORRECTION_COMMAND, null);
            throw error;
        }
        editor.dispatchCommand(SAVE_ACTIVE_CORRECTION_COMMAND, null);

        await this.waitForEditToFinish(sceneGroupId);
    }

    /** Ordered, real (non-space, non-silence) transcript word nodes, root-level. */
    private static wordNodesInOrder(): TranscriptWordNode[] {
        return $getRoot()
            .getAllTextNodes()
            .filter(
                (node): node is TranscriptWordNode =>
                    $isTranscriptWordNode(node) &&
                    $isTranscriptWordNodeAndNotSilence(node) &&
                    !$isSpace(node),
            );
    }

    /** Select the matched word run so START_CORRECT_TEXT_COMMAND has a valid range. */
    private async selectWordRun(editor: LexicalEditor, findPhrase: string): Promise<void> {
        await updateEditor(editor, () => {
            const words = EditAIVoiceoverTool.wordNodesInOrder();
            const matches = findAllWordSequenceMatches(
                words.map((node) => ({ word: node.getTextContent() })),
                [findPhrase],
            );
            if (matches.length !== 1) {
                throw new Error('could not locate the phrase in the transcript editor.');
            }
            const start = words[matches[0].startIndex];
            const end = words[matches[0].endIndex];
            const selection = $createRangeSelection();
            selection.setTextNodeRange(start, 0, end, end.getTextContent().length);
            $setSelection(selection);
        });
        editor.dispatchCommand(SELECTION_CHANGE_COMMAND, undefined);
    }

    /**
     * Replace the matched word nodes' text inside the active correction node (created by
     * START_CORRECT_TEXT_COMMAND). When the word counts match we swap word-for-word;
     * otherwise the first matched node receives the full replacement text (the backend
     * re-segments it) and the remaining matched words are removed.
     */
    private async replaceWordsInCorrectionNode(
        editor: LexicalEditor,
        findPhrase: string,
        replacement: string,
    ): Promise<void> {
        const replacementWords = replacement.split(/\s+/).filter((word) => word !== '');

        await updateEditor(editor, () => {
            const correctionNode = $getRoot().getChildren().find($isTextCorrectElementNode);
            if (!correctionNode) {
                throw new Error('text correction mode did not start.');
            }
            const wordNodes = $dfsWithinNode(correctionNode).filter(
                (node): node is TranscriptWordNode =>
                    $isTranscriptWordNode(node) &&
                    $isTranscriptWordNodeAndNotSilence(node) &&
                    !$isSpace(node),
            );
            const matches = findAllWordSequenceMatches(
                wordNodes.map((node) => ({ word: node.getTextContent() })),
                [findPhrase],
            );
            if (matches.length !== 1) {
                throw new Error('could not locate the phrase to replace after entering edit mode.');
            }
            const { startIndex, endIndex } = matches[0];
            const matchedNodes = wordNodes.slice(startIndex, endIndex + 1);

            if (replacementWords.length === matchedNodes.length) {
                matchedNodes.forEach((node, i) => node.setTextContent(replacementWords[i]));
            } else {
                matchedNodes[0].setTextContent(replacementWords.join(' '));
                matchedNodes.slice(1).forEach((node) => node.remove());
            }
        });
    }

    /**
     * Wait for the re-synthesis kicked off by saving the correction to complete. Saving
     * first calls the text-segmentation backend, then editAIVoiceoverOperation marks the
     * scene group loading and clears it when reconciliation finishes.
     */
    private async waitForEditToFinish(sceneGroupId: string): Promise<void> {
        // Wait for loading to begin (segmentation runs first, so this isn't immediate).
        for (let attempt = 0; attempt < 200; attempt++) {
            if (isSceneGroupLoading(this.getState(), sceneGroupId)) {
                break;
            }
            await delay(25);
        }
        // Wait for reconciliation + checkpoint to finish.
        for (let attempt = 0; attempt < 2400; attempt++) {
            if (!isSceneGroupLoading(this.getState(), sceneGroupId)) {
                return;
            }
            await delay(25);
        }
        throw new Error('the voiceover re-synthesis did not finish in time.');
    }
}
